import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cacheWorkoutsOffline } from "@/lib/offlineQueue";

interface StudentData {
  student: { id?: string; name: string; email?: string; phone?: string; photo_url: string | null };
  tenant?: { id: string; name: string; logo_url?: string | null; brand_color_hsl?: string | null } | null;
  workouts: any[];
  completed: any[];
  completed_count: number;
  latest_assessment: any;
  assessments?: any[];
  has_anamnese: boolean;
  has_pending_workouts?: boolean;
  access_token?: string;
}

interface StudentDataContextType {
  data: StudentData | null;
  loading: boolean;
  error: string;
  offline: boolean;
  sessionExpired: boolean;
  loadData: (token: string, force?: boolean) => Promise<StudentData | null>;
  setData: (data: StudentData | null) => void;
}

const StudentDataContext = createContext<StudentDataContextType | null>(null);

const CACHE_TTL = 180_000; // 3 minutes
const OFFLINE_CACHE_KEY = "student_offline_data";
const OFFLINE_CACHE_TS_KEY = "student_offline_ts";

function saveToOfflineCache(data: StudentData) {
  try {
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(OFFLINE_CACHE_TS_KEY, Date.now().toString());
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function loadFromOfflineCache(): StudentData | null {
  try {
    const raw = localStorage.getItem(OFFLINE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StudentData;
  } catch {
    return null;
  }
}

function getOfflineCacheAge(): number {
  const ts = localStorage.getItem(OFFLINE_CACHE_TS_KEY);
  if (!ts) return Infinity;
  return Date.now() - parseInt(ts, 10);
}

export function StudentDataProvider({ children }: { children: ReactNode }) {
  const [data, setDataState] = useState<StudentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [sessionExpired, setSessionExpired] = useState(false);
  const lastFetchRef = useRef<{ token: string; time: number } | null>(null);
  const inflightRef = useRef<Promise<StudentData | null> | null>(null);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Detect auth session expiry (e.g. refresh token revoked)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
        if (event === "SIGNED_OUT") {
          setSessionExpired(true);
          setDataState(null);
          setError("Sessão expirada. Faça login novamente.");
          localStorage.removeItem("student_token");
          localStorage.removeItem(OFFLINE_CACHE_KEY);
          localStorage.removeItem(OFFLINE_CACHE_TS_KEY);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const setData = useCallback((newData: StudentData | null) => {
    setDataState(newData);
    if (newData) {
      saveToOfflineCache(newData);
      if (newData.workouts?.length) cacheWorkoutsOffline(newData.workouts);
    }
  }, []);

  const loadData = useCallback(async (token: string, force = false): Promise<StudentData | null> => {
    // Return cached if fresh
    const now = Date.now();
    if (
      !force &&
      lastFetchRef.current &&
      lastFetchRef.current.token === token &&
      now - lastFetchRef.current.time < CACHE_TTL &&
      data
    ) {
      return data;
    }

    // Deduplicate in-flight requests (skip dedup when forcing)
    if (!force && inflightRef.current) return inflightRef.current;

    // If offline, serve from localStorage cache immediately
    if (!navigator.onLine) {
      const cached = loadFromOfflineCache();
      if (cached) {
        setDataState(cached);
        setError("");
        setOffline(true);
        return cached;
      }
      setError("Sem conexão e sem dados em cache");
      return null;
    }

    setLoading(true);
    setError("");

    const promise = (async () => {
      try {
        const { data: res, error: err } = await supabase.functions.invoke("student-data", {
          body: { token },
        });
        if (err) throw err;
        if (res?.error) throw new Error(res.error);
        setDataState(res);
        saveToOfflineCache(res);
        if (res.workouts?.length) cacheWorkoutsOffline(res.workouts);
        lastFetchRef.current = { token, time: Date.now() };
        return res as StudentData;
      } catch (e: any) {
        const msg = e?.message || "Erro ao carregar dados";
        const isNotFound = msg.includes("não encontrado") || msg.includes("Acesso não encontrado") || msg.includes("Token expirado");
        
        // If student was deleted or token invalid, clear cache and show error
        if (isNotFound) {
          localStorage.removeItem(OFFLINE_CACHE_KEY);
          localStorage.removeItem(OFFLINE_CACHE_TS_KEY);
          setDataState(null);
          setError("Conta não encontrada. Seu acesso pode ter sido removido.");
          return null;
        }
        
        // On network failure only, try offline cache
        const cached = loadFromOfflineCache();
        if (cached) {
          setDataState(cached);
          setOffline(true);
          setError("");
          return cached;
        }
        setError(msg);
        return null;
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();

    inflightRef.current = promise;
    return promise;
  }, [data]);

  return (
    <StudentDataContext.Provider value={{ data, loading, error, offline, sessionExpired, loadData, setData }}>
      {children}
    </StudentDataContext.Provider>
  );
}

export function useStudentData() {
  const ctx = useContext(StudentDataContext);
  if (!ctx) throw new Error("useStudentData must be used within StudentDataProvider");
  return ctx;
}
